import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Rating from '@mui/material/Rating'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'

import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_COMPACT_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import Pagination from '../../component/Pagination'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget, getLocationState, parseNumberParam } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'
import {
    applyAppSportsReviewDraft,
    clearAppSportsReviewDraft,
    createAppSportsReviewDraftPayload,
    NEW_APP_SPORTS_REVIEW_DRAFT_KEY,
    readAppSportsReviewDraft,
    saveAppSportsReviewDraft,
} from './appSportsReviewDraftUtils'

const DEFAULT_PICKER_SEARCH_SELECT = {
    searchType: 99,
}

const PICKER_SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_PICKER_SEARCH_SELECT.searchType,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
}

export default function NewAppSportsReview() {
    const location = useLocation()
    const history = useHistory()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || { searchStatus: 99, searchPopupStatus: '' }
    const listSearch = locationState.listSearch || ''
    const initialPickerStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_PICKER_SEARCH_SELECT,
        selectParams: PICKER_SEARCH_SELECT_PARAMS,
    }))
    const initialPickerState = initialPickerStateRef.current
    const [totalCount, setTotalCount] = useState(0)
    const [curPage, setCurPage] = useState(initialPickerState.curPage)
    const offset = 5
    const [saving, setSaving] = useState(false)
    const [datas, setDatas] = useState([])
    const [params, setParams] = useState({
        sportsId: null,
        thumbnail: '',
        title: '',
        address: '',
        star: 1,
        content: '',
    })
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [searchText, setSearchText] = useState(initialPickerState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialPickerState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialPickerState.searchSelect)
    const [draftReady, setDraftReady] = useState(false)
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const storedDraft = readAppSportsReviewDraft(NEW_APP_SPORTS_REVIEW_DRAFT_KEY)
        const restoredDraft = applyAppSportsReviewDraft({
            baseParams: {
                sportsId: null,
                thumbnail: '',
                title: '',
                address: '',
                star: 1,
                content: '',
            },
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppSportsReviewDraftPayload({
            params: {
                sportsId: null,
                thumbnail: '',
                title: '',
                address: '',
                star: 1,
                content: '',
            },
        }))

        setParams(restoredDraft.params)
        setDraftReady(true)
    }, [])

    const getData = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/sports/getList', { page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect })
            if (data.code === 200) {
                setTotalCount(data.data.count.length)
                setDatas(data.data.rows)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('운동시설 목록을 불러오지 못했습니다.')
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: PICKER_SEARCH_SELECT_PARAMS,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // App 운동시설 리뷰 등록은 시설 선택과 리뷰 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppSportsReviewDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppSportsReviewDraft(NEW_APP_SPORTS_REVIEW_DRAFT_KEY)
            return
        }

        saveAppSportsReviewDraft(NEW_APP_SPORTS_REVIEW_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (saving) {
            return
        }

        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
        setSearchQuery((searchText || '').trim())
        setCurPage(1)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (saving) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_PICKER_SEARCH_SELECT,
        })
    }

    const handleUpdateField = (e) => {
        if (e.target.name === 'star') {
            setParams({
                ...params,
                [e.target.name]: Number(e.target.value)
            })
        } else {
            setParams({
                ...params,
                [e.target.name]: e.target.value
            })
        }
    }

    const createData = async () => {
        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appSportsReview/create', { sportsId: params.sportsId, star: params.star, content: params.content })

            if (data.code === 200) {
                // 등록 후에도 운영자가 보던 App 운동시설 리뷰 목록 조건으로 그대로 복귀시킨다.
                clearAppSportsReviewDraft(NEW_APP_SPORTS_REVIEW_DRAFT_KEY)
                history.push(createListLinkTarget({
                    pathname: '/appSportsReviews',
                    search: listSearch,
                    openSnackbar: true,
                    severitySnackbar: 'success',
                    messageSnackbar: data.message,
                    curPage: listCurPage,
                    searchText: listSearchText,
                    searchSelect: listSearchSelect
                }))
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('운동시설 리뷰를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        if (!params.sportsId) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('헬스장을 선택하세요')
            return false
        }
        createData()
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (saving) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData()
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const chooseButtonClick = (sportsId, thumbnail, title, address) => {
        setParams({
            ...params,
            sportsId: sportsId, 
            thumbnail: thumbnail,
            title: title,
            address: address
        })
    }
    
    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>APP 운동시설 리뷰 등록</Title>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={saving} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="시설명"
                                type="search"
                                name="searchText"
                                value={searchText}
                                disabled={saving}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={saving}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="요금타입"
                                select
                                name="searchType"
                                value={searchSelect.searchType}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={saving}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={99}>전체</MenuItem>
                                <MenuItem value={4}>요금제(상)</MenuItem>
                                <MenuItem value={3}>요금제(중)</MenuItem>
                                <MenuItem value={2}>요금제(하)</MenuItem>
                                <MenuItem value={1}>무료</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    
                    <Table size="small">
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>요금타입</TableCell>
                                <TableCell>타이틀</TableCell>
                                <TableCell>연락처</TableCell>
                                <TableCell align="left">주소</TableCell>
                                {/* <TableCell>운영시간</TableCell>
                                <TableCell align="center">가격안내</TableCell> */}
                                <TableCell>등록일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {datas.map((row) => (
                            <TableRow key={row.sportsId} sx={{ backgroundColor: row.sportsId === params.sportsId ? 'silver' : '' }}>
                                <TableCell>{row.rownum}</TableCell>
                                <TableCell><img className="listImg" src={`${process.env.REACT_APP_AWS_S3_URL}/${row.thumbnail}`} alt="" /></TableCell>
                                <TableCell>{row.type === 1 ? '무료' : row.type === 2 ? '요금제(하)' : row.type === 3 ? '요금제(중)' : '요금제(상)'}</TableCell>
                                <TableCell>{row.title}</TableCell>
                                <TableCell>{row.phone}</TableCell>
                                <TableCell align="left">{row.address + ' ' + row.addressDetail}</TableCell>
                                {/* <TableCell style={{ whiteSpace: 'pre-wrap' }} >{row.openingHours}</TableCell>
                                <TableCell align="center">{PriceTd(row.price)}</TableCell> */}
                                <TableCell>{renderDateTime(row.regTime)}</TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        sx={FORM_COMPACT_ACTION_BUTTON_SX}
                                        onClick={() => chooseButtonClick(row.sportsId, row.thumbnail, row.title, row.address)}
                                        disabled={saving}
                                    >
                                        선택
                                    </Button>
                                </TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                </Paper>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={4} md={4} sm={4}>
                            <FormControl fullWidth>
                                <TextField
                                    label="시설명"
                                    required
                                    id="title"
                                    name="title"
                                    value={params.title || ''}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} md={8} sm={8}>
                            <FormControl fullWidth>
                                <TextField
                                    label="주소"
                                    id="address"
                                    name="address"
                                    value={params.address || ''}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl component="fieldset">
                                <Typography component="legend">별점</Typography>
                                <Rating
                                    id="star"
                                    name="star"
                                    value={params.star || 1}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="내용"
                                    id="content"
                                    name="content"
                                    multiline
                                    rows={15}
                                    value={params.content || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/appSportsReviews"
                        search={listSearch}
                        visible={true}
                        curPage={listCurPage}
                        searchText={listSearchText}
                        searchSelect={listSearchSelect}
                        onBackClick={() => clearAppSportsReviewDraft(NEW_APP_SPORTS_REVIEW_DRAFT_KEY)}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
